import { create } from 'zustand';

/**
 * Theme system: light / dark / system. `system` follows the OS `prefers-color-scheme`. The whole
 * app re-themes by swapping CSS token variables under the `.dark` class (see generated tokens.css)
 * - no per-component dark variants. A no-FOUC inline script in index.html applies the class before
 * React mounts; this store keeps it in sync afterward.
 */
export type ThemeMode = 'light' | 'dark' | 'system';
export const THEME_STORAGE_KEY = 'aabar.theme';

export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolveTheme(mode) === 'dark');
}

function storedMode(): ThemeMode {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_STORAGE_KEY) : null;
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  mode: storedMode(),
  setMode: (mode) => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    applyTheme(mode);
    set({ mode });
  },
}));
